import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Settings, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const ImagenDXCases = ({ user, onLogout }) => {
  const { system } = useParams();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [passedCases, setPassedCases] = useState([]);
  const [passCounts, setPassCounts] = useState({});

  useEffect(() => {
    fetchCases();
    fetchProgress();
    fetchPassCounts();
  }, [system]);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/imagendx/cases', {
        params: { system: decodeURIComponent(system) }
      });
      setCases(response.data);
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast.error('Error al cargar los casos');
    } finally {
      setLoading(false);
    }
  };

  const fetchPassCounts = async () => {
    try {
      const response = await apiClient.get('/progress/pass-counts');
      setPassCounts(response.data.pass_counts || {});
    } catch (error) {
      console.error('Error fetching pass counts:', error);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await apiClient.get('/progress/imagendx');
      setPassedCases(response.data.passed || []);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const isPassed = (caseId) => passedCases.includes(caseId);

  const playPencilSound = () => {
    try {
      const audio = new Audio('https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/wfi9fn7o_pencil_scribble_7-389260.mp3');
      audio.volume = 1.0;
      audio.play();
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  const handleCaseClick = (caseId) => {
    playPencilSound();
    setTimeout(() => {
      navigate(`/imagen-dx/game/${caseId}`);
    }, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button
            onClick={() => navigate('/imagen-dx')}
            className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Volver</span>
          </button>
          
          <div className="flex items-center space-x-2">
            {user?.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
              >
                <Settings size={14} className="text-black" />
              </button>
            )}
            
            <button
              onClick={onLogout}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
            >
              <LogOut size={14} className="text-black" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-black mb-6 text-center">
            {decodeURIComponent(system)}
          </h1>

          {cases.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">No hay casos disponibles aún</p>
              <button
                onClick={() => navigate('/imagen-dx')}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Volver a Sistemas
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {cases.map((caseItem, index) => {
                const isHovered = hoveredItem === caseItem.id;
                const passed = isPassed(caseItem.id);
                const passCount = passCounts[caseItem.id] || 0;
                
                return (
                  <div key={caseItem.id} className="relative">
                    {/* Pass count badge - top right */}
                    {passCount > 0 && (
                      <div className="absolute -top-2 -right-2 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-white border-2 border-blue-500">
                        <span className="text-xs font-bold text-blue-500">{passCount}</span>
                      </div>
                    )}
                    
                    <button
                      onClick={() => handleCaseClick(caseItem.id)}
                      onMouseEnter={() => setHoveredItem(caseItem.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`w-full border rounded-xl p-4 transition-all duration-200 text-left flex items-start space-x-3 ${
                        passed
                          ? 'bg-gray-100 border-gray-200'
                          : isHovered
                            ? 'bg-white border-black shadow-md'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {/* Circle indicator */}
                      <div className="flex-shrink-0 mt-1">
                        <span 
                          className={`relative flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                            passed
                              ? 'border-gray-400'
                              : isHovered ? 'border-black' : 'border-gray-400'
                          }`}
                        >
                          <span 
                            className={`absolute w-3 h-3 rounded-full transition-all duration-200 ${
                              passed
                                ? 'bg-gray-400 scale-100 opacity-100'
                                : isHovered ? 'bg-black scale-100 opacity-100' : 'scale-0 opacity-0'
                            }`}
                          ></span>
                        </span>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm sm:text-base font-semibold leading-snug transition-colors duration-200 ${
                          passed
                            ? 'text-gray-500'
                            : isHovered ? 'text-black' : 'text-gray-800'
                        }`}>
                          Caso #{index + 1}: {caseItem.modality}
                        </h3>
                        <p className={`text-xs sm:text-sm mt-1 line-clamp-2 ${passed ? 'text-gray-400' : 'text-gray-500'}`}>
                          {caseItem.caso_clinico}
                        </p>
                      </div>
                      
                      {/* Checkmark for passed or Arrow */}
                      <div className="flex-shrink-0 self-center">
                        {passed ? (
                          <Check 
                            size={20} 
                            className="text-green-500"
                            strokeWidth={3}
                          />
                        ) : (
                          <svg 
                            className={`w-5 h-5 transition-all duration-200 ${
                              isHovered ? 'text-black translate-x-1' : 'text-gray-400'
                            }`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </div>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImagenDXCases;
